import { forwardRef, useEffect, useState } from 'react';
import type { EventoEstado } from '../api/evento';
import { t } from '../i18n/idioma';
import { Icone } from './kit';

interface Props {
  estado: EventoEstado | null;
  aberto?: boolean;
  /** O próximo evento agendado (techspec-eventos §02.2): o selo aparece na última hora antes dele. */
  proximo?: { readonly hora: string; readonly emMs: number; readonly tipo?: string } | null;
  onClick(): void;
}

const JANELA_PROXIMO_MS = 60 * 60_000;

const GRACE_INSCRICAO_MS = 5_000;
const GRACE_PAREAR_MS = 120_000;

export function eventoEstaRolando(estado: EventoEstado | null | undefined): boolean {
  if (!estado || estado.estado === 0) return false;
  const agora = Date.now();
  if (estado.estado === 1) return estado.fechaEmMs > agora - GRACE_INSCRICAO_MS;
  if (estado.fimEmMs > 0) return estado.fimEmMs > agora;
  return estado.fechaEmMs > agora - GRACE_PAREAR_MS;
}

export const EventoSelo = forwardRef<HTMLButtonElement, Props>(function EventoSelo(
  { estado, aberto, proximo, onClick },
  ref
) {
  const [, setTick] = useState(0);
  const rolando = eventoEstaRolando(estado);
  const emBreve = !rolando && !!proximo && proximo.emMs - Date.now() > 0 && proximo.emMs - Date.now() <= JANELA_PROXIMO_MS;
  useEffect(() => {
    if (!rolando && !emBreve) return;
    const id = window.setInterval(() => setTick(n => n + 1), rolando ? 250 : 15_000);
    return () => window.clearInterval(id);
  }, [rolando, emBreve, estado?.fechaEmMs, estado?.fimEmMs]);

  if (emBreve && proximo) {
    const faltam = Math.max(0, Math.floor((proximo.emMs - Date.now()) / 1000));
    return (
      <button ref={ref} type="button" className="evento-selo evento-selo--agenda" aria-pressed={!!aberto} onClick={onClick}>
        <span className="evento-selo__ico"><Icone nome="evento" tam={22} /></span>
        <span className="evento-selo__txt">
          <span className="evento-selo__nome">{t(proximo.tipo === 'chefe-mundo' ? 'chefe.nome' : 'evento.selo.titulo')}</span>
          <span className="evento-selo__quando">{t('evento.selo.proximo', { hora: proximo.hora })}</span>
        </span>
        <span className="evento-selo__n">{mmss(faltam)}</span>
      </button>
    );
  }

  if (!rolando || !estado) return null;

  const alvo = estado.estado === 1 ? estado.fechaEmMs : estado.fimEmMs;
  const s = alvo ? Math.max(0, Math.floor((alvo - Date.now()) / 1000)) : 0;
  const tom = s <= 10 ? 'vermelho' : s <= 30 || (estado.estado === 2 && s <= 180) ? 'ambar' : 'normal';
  const naLuta = estado.estado !== 1;
  const quando = naLuta ? t('evento.selo.termina') : t('evento.selo.entra');

  return (
    <button
      ref={ref}
      type="button"
      className={`evento-selo evento-selo--${tom}`}
      aria-pressed={!!aberto}
      onClick={onClick}
    >
      <span className="evento-selo__ico"><Icone nome="evento" tam={22} /></span>
      <span className="evento-selo__txt">
        <span className="evento-selo__nome">{t('evento.selo.titulo')}</span>
        <span className="evento-selo__quando">{quando}</span>
      </span>
      <span className="evento-selo__n">{mmss(s)}</span>
      {naLuta && (
        <span className="evento-selo__vivos" title={t('evento.selo.placar', { n: estado.vivos })}>
          <span className="kit-pip kit-pip--vivo" />
          <span className={`kit-pip kit-pip--vivo${estado.vivos < 2 ? ' is-off' : ''}`} />
        </span>
      )}
    </button>
  );
});

function mmss(s: number): string {
  const m = Math.floor(s / 60);
  return `${m}:${(s % 60).toString().padStart(2, '0')}`;
}
